import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Zap, CheckCircle, Ship, Factory, Waves } from 'lucide-react';

const Projects = () => {
  const { t } = useTranslation();

  const categories = [
    {
      icon: Ship,
      title: t('projects.categories.floating.title'),
      subtitle: t('projects.categories.floating.subtitle'),
      desc: t('projects.categories.floating.desc'),
      color: 'blue',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/p2age42h_WhatsApp%20Image%202026-06-02%20at%2002.31.34.jpeg'
    },
    {
      icon: Waves,
      title: t('projects.categories.offshore.title'),
      subtitle: t('projects.categories.offshore.subtitle'),
      desc: t('projects.categories.offshore.desc'),
      color: 'cyan',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/aqxoueyy_marine%20construction%20gorsel.png'
    },
    {
      icon: Factory,
      title: t('projects.categories.onshore.title'),
      subtitle: t('projects.categories.onshore.subtitle'),
      desc: t('projects.categories.onshore.desc'),
      color: 'orange',
      image: 'https://images.unsplash.com/photo-1473876637954-4b493d59fd97?w=800&q=80'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="projects-title">
              {t('projects.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('projects.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Project */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-900 to-slate-900 text-white rounded-2xl overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Content */}
              <div className="p-8 lg:p-12 space-y-6">
                <div className="inline-block px-4 py-2 bg-amber-500 text-slate-900 font-bold rounded-full text-sm">
                  {t('projects.hero.badge')}
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">
                  {t('projects.hero.name')}
                </h2>

                {/* Project Details */}
                <div className="space-y-3 text-blue-100">
                  <div className="flex items-center space-x-3">
                    <MapPin size={20} className="text-amber-400 flex-shrink-0" />
                    <span><strong>{t('projects.hero.location')}:</strong> {t('projects.hero.location')}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar size={20} className="text-amber-400 flex-shrink-0" />
                    <span><strong>{t('projects.hero.period')}:</strong> {t('projects.hero.period')}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Zap size={20} className="text-amber-400 flex-shrink-0" />
                    <span><strong>{t('projects.hero.capacity')}:</strong> {t('projects.hero.capacity')}</span>
                  </div>
                </div>

                <p className="text-lg text-gray-200 leading-relaxed">
                  {t('projects.hero.desc')}
                </p>

                {/* Key Achievements */}
                <div>
                  <h3 className="text-xl font-bold mb-4">
                    {t('projects.hero.responsibilities.title')}
                  </h3>
                  <ul className="space-y-2">
                    {t('projects.hero.responsibilities.items', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-200">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Senegal LNG Project Image */}
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/w0h0dbf5_No%20Name%20FSRU.jpeg"
                  alt="Senegal LNG-to-Power Project - FSRU Vessel"
                  className="w-full h-full object-cover min-h-[500px]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Categories */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all overflow-hidden group"
                >
                  {/* Image Header */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
                    <Icon className="absolute top-4 left-4 text-white drop-shadow-lg" size={36} />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="mb-3">
                      <h3 className="text-2xl font-bold text-slate-900">
                        {category.title}
                      </h3>
                      <p className="text-amber-600 font-semibold text-sm">
                        {category.subtitle}
                      </p>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      {category.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Completed Projects with Real Photos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('projects.completed.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('projects.completed.subtitle')}
            </p>
          </div>

          <div className="space-y-12">
            {/* Senegal Onshore Pipeline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-[450px] lg:h-[500px] overflow-hidden bg-slate-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/6bujjqhx_offshore%20and%20marine%20infrastructure.jpeg"
                    alt="Senegal Onshore Gas Pipeline Installation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="inline-block px-3 py-1 bg-blue-100 text-blue-900 font-semibold rounded-full text-xs mb-3 self-start">
                    {t('projects.senegalPipeline.scope')}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('projects.senegalPipeline.title')}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <MapPin size={16} className="text-amber-500 mr-2" />
                    {t('projects.senegalPipeline.location')}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {t('projects.senegalPipeline.desc')}
                  </p>
                  <ul className="space-y-2">
                    {t('projects.senegalPipeline.highlights', { returnObjects: true }).map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Dakar Fishing Harbour */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="p-8 lg:p-10 order-2 lg:order-1 flex flex-col justify-center">
                  <div className="inline-block px-3 py-1 bg-amber-100 text-amber-900 font-semibold rounded-full text-xs mb-3">
                    {t('projects.dakarHarbour.scope')}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('projects.dakarHarbour.title')}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <MapPin size={16} className="text-amber-500 mr-2" />
                    {t('projects.dakarHarbour.location')}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {t('projects.dakarHarbour.desc')}
                  </p>
                  <ul className="space-y-2">
                    {t('projects.dakarHarbour.highlights', { returnObjects: true }).map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative h-[450px] lg:h-[500px] overflow-hidden order-1 lg:order-2 bg-slate-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/z8v3a1mj_Fishing%20Harbour%20Rehabilitaion.png"
                    alt="Dakar Fishing Harbour Rehabilitation - Aerial View"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            {/* Gabon Transmission Tower Foundations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-[450px] lg:h-[500px] overflow-hidden bg-slate-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/p1ygitl2_direk%20temel.png"
                    alt="Gabon Energy Transmission Line Foundation Construction"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="inline-block px-3 py-1 bg-orange-100 text-orange-900 font-semibold rounded-full text-xs mb-3">
                    {t('projects.gabonTower.scope')}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('projects.gabonTower.title')}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <MapPin size={16} className="text-amber-500 mr-2" />
                    {t('projects.gabonTower.location')}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {t('projects.gabonTower.desc')}
                  </p>
                  <ul className="space-y-2">
                    {t('projects.gabonTower.highlights', { returnObjects: true }).map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;